/**
 * AdvokatCRM - Hovedapplikasjon
 */

import React, { useState, useEffect } from 'react';
import Dashboard from './pages/Dashboard';
import SakListe from './pages/SakListe';
import SakTidslinje from './pages/SakTidslinje';
import NySak from './pages/NySak';
import RedigerSak from './pages/RedigerSak';
import SakDetaljer from './pages/SakDetaljer';
import StatusGjennomgang from './pages/StatusGjennomgang';
import KlientListe from './pages/KlientListe';
import KYCOversikt from './pages/KYCOversikt';
import KYCSkjema from './pages/KYCSkjema';
import Fristoversikt from './pages/Fristoversikt';
import Oppgaveoversikt from './pages/Oppgaveoversikt';
import RettshjelpOversikt from './pages/RettshjelpOversikt';
import SakerUtenRettshjelp from './pages/SakerUtenRettshjelp';
import Innstillinger from './pages/Innstillinger';
import Login from './pages/Login';
import TaskPlanner from './components/TaskPlanner';
import OppgaveProdusent from './components/OppgaveProdusent';
import HjelpSide from './components/HjelpSide';
import DailyBriefing from './components/DailyBriefing';
import UfakturertTid from './components/UfakturertTid';
import ErrorBoundary from './components/ErrorBoundary';
import VarslingSystem from './components/VarslingSystem';
import './AppCRM.css';

export default function AppCRM() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedSakId, setSelectedSakId] = useState(null);
  const [kycToken, setKycToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHandlingCallback, setIsHandlingCallback] = useState(false);

  // Sjekk om bruker er logget inn
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      } catch (e) {
        console.error('Kunne ikke parse lagret bruker:', e);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // Enkel routing basert på URL
  useEffect(() => {
    const path = window.location.pathname;

    // Microsoft OAuth callback
    if (path === '/auth/callback') {
      setIsHandlingCallback(true);
      handleMicrosoftCallback();
      return;
    }

    if (path === '/' || path === '/dashboard') {
      setCurrentPage('dashboard');
    } else if (path === '/tidslinje') {
      setCurrentPage('tidslinje');
    } else if (path === '/saker') {
      setCurrentPage('saker');
    } else if (path === '/saker/statusgjennomgang') {
      setCurrentPage('statusgjennomgang');
    } else if (path === '/saker/ny') {
      setCurrentPage('ny-sak');
    } else if (path.startsWith('/saker/') && path.includes('/rediger')) {
      const sakId = path.split('/')[2];
      setSelectedSakId(sakId);
      setCurrentPage('rediger-sak');
    } else if (path.startsWith('/saker/')) {
      const sakId = path.split('/')[2];
      setSelectedSakId(sakId);
      setCurrentPage('sak-detaljer');
    } else if (path === '/frister') {
      setCurrentPage('frister');
    } else if (path === '/oppgaveoversikt') {
      setCurrentPage('oppgaveoversikt');
    } else if (path === '/klienter') {
      setCurrentPage('klienter');
    } else if (path.startsWith('/kyc-skjema/')) {
      const token = path.split('/')[2];
      setKycToken(token);
      setCurrentPage('kyc-skjema');
    } else if (path === '/kyc') {
      setCurrentPage('kyc');
    } else if (path === '/rettshjelp') {
      setCurrentPage('rettshjelp');
    } else if (path === '/rettshjelp/uten-dekning') {
      setCurrentPage('rettshjelp-uten');
    } else if (path === '/oppgaver' || path === '/planner') {
      setCurrentPage('planner');
    } else if (path === '/oppgave-produsent') {
      setCurrentPage('oppgave-produsent');
    } else if (path === '/briefing') {
      setCurrentPage('briefing');
    } else if (path === '/ufakturert-tid') {
      setCurrentPage('ufakturert-tid');
    } else if (path === '/innstillinger') {
      setCurrentPage('innstillinger');
    } else if (path === '/hjelp') {
      setCurrentPage('hjelp');
    }

    // Lytt til popstate (tilbake-knapp)
    const handlePopState = () => {
      const newPath = window.location.pathname;
      if (newPath === '/' || newPath === '/dashboard') {
        setCurrentPage('dashboard');
      } else if (newPath === '/tidslinje') {
        setCurrentPage('tidslinje');
      } else if (newPath === '/saker') {
        setCurrentPage('saker');
      } else if (newPath === '/saker/statusgjennomgang') {
        setCurrentPage('statusgjennomgang');
      } else if (newPath === '/saker/ny') {
        setCurrentPage('ny-sak');
      } else if (newPath.startsWith('/saker/') && newPath.includes('/rediger')) {
        const sakId = newPath.split('/')[2];
        setSelectedSakId(sakId);
        setCurrentPage('rediger-sak');
      } else if (newPath.startsWith('/saker/')) {
        const sakId = newPath.split('/')[2];
        setSelectedSakId(sakId);
        setCurrentPage('sak-detaljer');
      } else if (newPath === '/frister') {
        setCurrentPage('frister');
      } else if (newPath === '/oppgaveoversikt') {
        setCurrentPage('oppgaveoversikt');
      } else if (newPath === '/klienter') {
        setCurrentPage('klienter');
      } else if (newPath.startsWith('/kyc-skjema/')) {
        const token = newPath.split('/')[2];
        setKycToken(token);
        setCurrentPage('kyc-skjema');
      } else if (newPath === '/kyc') {
        setCurrentPage('kyc');
      } else if (newPath === '/rettshjelp') {
        setCurrentPage('rettshjelp');
      } else if (newPath === '/rettshjelp/uten-dekning') {
        setCurrentPage('rettshjelp-uten');
      } else if (newPath === '/oppgaver' || newPath === '/planner') {
        setCurrentPage('planner');
      } else if (newPath === '/oppgave-produsent') {
        setCurrentPage('oppgave-produsent');
      } else if (newPath === '/briefing') {
        setCurrentPage('briefing');
      } else if (newPath === '/ufakturert-tid') {
        setCurrentPage('ufakturert-tid');
      } else if (newPath === '/innstillinger') {
        setCurrentPage('innstillinger');
      } else if (newPath === '/hjelp') {
        setCurrentPage('hjelp');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigerTil = (side, sakId = null) => {
    if (side === 'dashboard') {
      window.history.pushState({}, '', '/dashboard');
      setCurrentPage('dashboard');
    } else if (side === 'tidslinje') {
      window.history.pushState({}, '', '/tidslinje');
      setCurrentPage('tidslinje');
    } else if (side === 'saker') {
      window.history.pushState({}, '', '/saker');
      setCurrentPage('saker');
    } else if (side === 'statusgjennomgang') {
      window.history.pushState({}, '', '/saker/statusgjennomgang');
      setCurrentPage('statusgjennomgang');
    } else if (side === 'sak-detaljer' && sakId) {
      window.history.pushState({}, '', `/saker/${sakId}`);
      setSelectedSakId(sakId);
      setCurrentPage('sak-detaljer');
    } else if (side === 'rediger-sak' && sakId) {
      window.history.pushState({}, '', `/saker/${sakId}/rediger`);
      setSelectedSakId(sakId);
      setCurrentPage('rediger-sak');
    } else if (side === 'frister') {
      window.history.pushState({}, '', '/frister');
      setCurrentPage('frister');
    } else if (side === 'oppgaveoversikt') {
      window.history.pushState({}, '', '/oppgaveoversikt');
      setCurrentPage('oppgaveoversikt');
    } else if (side === 'klienter') {
      window.history.pushState({}, '', '/klienter');
      setCurrentPage('klienter');
    } else if (side === 'kyc') {
      window.history.pushState({}, '', '/kyc');
      setCurrentPage('kyc');
    } else if (side === 'rettshjelp') {
      window.history.pushState({}, '', '/rettshjelp');
      setCurrentPage('rettshjelp');
    } else if (side === 'rettshjelp-uten') {
      window.history.pushState({}, '', '/rettshjelp/uten-dekning');
      setCurrentPage('rettshjelp-uten');
    } else if (side === 'planner') {
      window.history.pushState({}, '', '/oppgaver');
      setCurrentPage('planner');
    } else if (side === 'oppgave-produsent') {
      window.history.pushState({}, '', '/oppgave-produsent');
      setCurrentPage('oppgave-produsent');
    } else if (side === 'briefing') {
      window.history.pushState({}, '', '/briefing');
      setCurrentPage('briefing');
    } else if (side === 'ufakturert-tid') {
      window.history.pushState({}, '', '/ufakturert-tid');
      setCurrentPage('ufakturert-tid');
    } else if (side === 'innstillinger') {
      window.history.pushState({}, '', '/innstillinger');
      setCurrentPage('innstillinger');
    } else if (side === 'hjelp') {
      window.history.pushState({}, '', '/hjelp');
      setCurrentPage('hjelp');
    }
  };

  const handleMicrosoftCallback = async () => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      alert('Microsoft-innlogging avbrutt');
      window.location.href = '/';
      return;
    }

    if (!code || !state) {
      alert('Mangler authorization code eller state');
      window.location.href = '/';
      return;
    }

    try {
      const response = await fetch(`https://advokat-crm-api.stig-958.workers.dev/auth/callback?code=${code}&state=${state}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Microsoft authentication failed');
      }

      const data = await response.json();

      // Lagre token og bruker
      const token = data.token;
      if (!token) {
        throw new Error('Ingen token mottatt fra server');
      }

      localStorage.setItem('access_token', token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setUser(data.user);
      setIsAuthenticated(true);

      // Redirect til dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Microsoft callback error:', error);
      console.error('Error details:', {
        message: error.message,
        code: code,
        state: state,
        apiUrl: 'https://advokat-crm-api.stig-958.workers.dev/auth/callback'
      });
      alert('Autentisering feilet: ' + error.message);
      window.location.href = '/';
    }
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setCurrentPage('dashboard');
    window.history.pushState({}, '', '/dashboard');
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Laster...</p>
      </div>
    );
  }

  if (isHandlingCallback) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Autentiserer med Microsoft 365...</p>
      </div>
    );
  }

  // KYC-skjema er offentlig (krever ikke innlogging)
  if (currentPage === 'kyc-skjema') {
    return <KYCSkjema token={kycToken} />;
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app-crm">
      {/* Navigation */}
      <nav className="app-nav">
        <div className="nav-brand">
          ⚖️ AdvokatCRM
        </div>
        <div className="nav-links">
          <button
            className={currentPage === 'dashboard' ? 'active' : ''}
            onClick={() => navigerTil('dashboard')}
          >
            📊 Dashboard
          </button>
          <button
            className={currentPage === 'briefing' ? 'active' : ''}
            onClick={() => navigerTil('briefing')}
          >
            🌅 Min dag
          </button>
          <button
            className={currentPage === 'ufakturert-tid' ? 'active' : ''}
            onClick={() => navigerTil('ufakturert-tid')}
          >
            ⏱️ Ufakturert tid
          </button>
          <button
            className={currentPage === 'saker' || currentPage === 'ny-sak' || currentPage === 'sak-detaljer' || currentPage === 'rediger-sak' ? 'active' : ''}
            onClick={() => navigerTil('saker')}
          >
            📁 Saker
          </button>
          <button
            className={currentPage === 'statusgjennomgang' ? 'active' : ''}
            onClick={() => navigerTil('statusgjennomgang')}
            title="Gå gjennom status på alle saker"
          >
            ✅ Statusgjennomgang
          </button>
          <button
            className={currentPage === 'tidslinje' ? 'active' : ''}
            onClick={() => navigerTil('tidslinje')}
          >
            📈 Tidslinje
          </button>
          <button
            className={currentPage === 'frister' ? 'active' : ''}
            onClick={() => navigerTil('frister')}
          >
            ⏰ Frister
          </button>
          <button
            className={currentPage === 'oppgaveoversikt' ? 'active' : ''}
            onClick={() => navigerTil('oppgaveoversikt')}
          >
            📋 Oppgaver
          </button>
          <button
            className={currentPage === 'planner' ? 'active' : ''}
            onClick={() => navigerTil('planner')}
          >
            📅 Planner
          </button>
          <button
            className={currentPage === 'oppgave-produsent' ? 'active' : ''}
            onClick={() => navigerTil('oppgave-produsent')}
          >
            🤖 Oppgave-produsent
          </button>
          <button
            className={currentPage === 'klienter' ? 'active' : ''}
            onClick={() => navigerTil('klienter')}
          >
            👥 Klienter
          </button>
          <button
            className={currentPage === 'kyc' ? 'active' : ''}
            onClick={() => navigerTil('kyc')}
          >
            ⚖️ KYC
          </button>
          <button
            className={currentPage === 'innstillinger' ? 'active' : ''}
            onClick={() => navigerTil('innstillinger')}
          >
            ⚙️ Innstillinger
          </button>
          <button
            className={currentPage === 'hjelp' ? 'active' : ''}
            onClick={() => navigerTil('hjelp')}
          >
            📚 Hjelp
          </button>
        </div>
        <div className="nav-user">
          <span>👤 {user?.displayName || user?.mail || 'Bruker'}</span>
          <button className="btn-logout" onClick={handleLogout}>
            Logg ut
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="app-main">
        {currentPage === 'dashboard' && <Dashboard navigerTil={navigerTil} />}
        {currentPage === 'briefing' && (
          <ErrorBoundary>
            <DailyBriefing />
          </ErrorBoundary>
        )}
        {currentPage === 'ufakturert-tid' && (
          <ErrorBoundary>
            <UfakturertTid />
          </ErrorBoundary>
        )}
        {currentPage === 'tidslinje' && <SakTidslinje navigerTil={navigerTil} />}
        {currentPage === 'saker' && <SakListe navigerTil={navigerTil} />}
        {currentPage === 'statusgjennomgang' && (
          <ErrorBoundary>
            <StatusGjennomgang navigerTil={navigerTil} />
          </ErrorBoundary>
        )}
        {currentPage === 'ny-sak' && <NySak navigerTil={navigerTil} />}
        {currentPage === 'rediger-sak' && <RedigerSak navigerTil={navigerTil} sakId={selectedSakId} />}
        {currentPage === 'sak-detaljer' && <SakDetaljer sakId={selectedSakId} navigerTil={navigerTil} />}
        {currentPage === 'frister' && (
          <ErrorBoundary>
            <Fristoversikt />
          </ErrorBoundary>
        )}
        {currentPage === 'oppgaveoversikt' && (
          <ErrorBoundary>
            <Oppgaveoversikt />
          </ErrorBoundary>
        )}
        {currentPage === 'planner' && (
          <ErrorBoundary>
            <TaskPlanner />
          </ErrorBoundary>
        )}
        {currentPage === 'oppgave-produsent' && (
          <ErrorBoundary>
            <OppgaveProdusent onOppgaverOpprettet={() => navigerTil('oppgaveoversikt')} />
          </ErrorBoundary>
        )}
        {currentPage === 'klienter' && (
          <ErrorBoundary>
            <KlientListe />
          </ErrorBoundary>
        )}
        {currentPage === 'kyc' && (
          <ErrorBoundary>
            <KYCOversikt />
          </ErrorBoundary>
        )}
        {currentPage === 'rettshjelp' && <RettshjelpOversikt navigerTil={navigerTil} />}
        {currentPage === 'rettshjelp-uten' && <SakerUtenRettshjelp />}
        {currentPage === 'innstillinger' && (
          <ErrorBoundary>
            <Innstillinger />
          </ErrorBoundary>
        )}
        {currentPage === 'hjelp' && (
          <ErrorBoundary>
            <HjelpSide />
          </ErrorBoundary>
        )}
      </main>

      {/* Varslingssystem - vises på alle sider */}
      <VarslingSystem />
    </div>
  );
}
